import { useState, useEffect } from 'react'
import { Save, Map, Store, FileText, Settings2, CreditCard, Eye, EyeOff, Shield, ArrowUpDown } from 'lucide-react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useLabelCurral } from '../../hooks/useLabelCurral'
import mapasService from '../../services/mapasService'

function Toggle({ checked, onChange, label, description }) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex-1 pr-4">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative flex-shrink-0 w-10 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}
      >
        <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
      </button>
    </div>
  )
}

function Section({ icon: Icon, title, subtitle, color = 'text-blue-600', children }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50">
        <div className={`p-1.5 rounded-lg bg-white border border-gray-200 ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-gray-800">{title}</h2>
          {subtitle && <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="px-5 divide-y divide-gray-100">
        {children}
      </div>
    </div>
  )
}

function ColumnGrid({ columns, activeColumns, configKey, set, config, maxColumns }) {
  const currentActive = (activeColumns || []).filter(c => columns.some(col => col.id === c))
  const totalAtivas = currentActive.length

  // Ordem natural das colunas (pela posição no array columns)
  const naturalOrder = columns.map(c => c.id)

  return (
    <div className="grid grid-cols-2 gap-2">
      {columns.map(col => {
        const isActive = (activeColumns || []).includes(col.id)
        const limitReached = maxColumns && totalAtivas >= maxColumns && !isActive
        return (
          <div
            key={col.id}
            className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${
              isActive ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'
            } ${col.obrigatoria || limitReached ? 'opacity-75 cursor-not-allowed' : 'hover:border-blue-300'}`}
            onClick={() => {
              if (col.obrigatoria) return
              if (limitReached && !isActive) return
              const current = activeColumns || []
              if (isActive) {
                set(configKey, current.filter(c => c !== col.id))
              } else {
                // Inserir na posição natural (ordem definida em columns)
                const updated = [...current, col.id].sort((a, b) => naturalOrder.indexOf(a) - naturalOrder.indexOf(b))
                set(configKey, updated)
              }
            }}
          >
            {isActive ? <Eye className="w-4 h-4 text-blue-600 flex-shrink-0" /> : <EyeOff className="w-4 h-4 text-gray-400 flex-shrink-0" />}
            <span className={`text-xs font-medium ${isActive ? 'text-blue-900' : 'text-gray-600'}`}>{col.label}</span>
            {col.obrigatoria && <span className="text-[10px] bg-yellow-100 text-yellow-800 px-1 rounded">Obrig.</span>}
            {limitReached && !isActive && <span className="text-[10px] bg-red-100 text-red-700 px-1 rounded">Máx.</span>}
          </div>
        )
      })}
    </div>
  )
}

function TabButton({ active, onClick, icon: Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all whitespace-nowrap ${
        active
          ? 'bg-blue-600 text-white shadow-sm'
          : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  )
}

export default function MapasConfig() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id
  const labelCurral = useLabelCurral()
  const isFluxoCavalo = organizadorAtivo?.fluxo_leilao === 'comissao_cavalo'
  // Fluxo cavalo não usa Mapa do Comprador/Vendedor (só Faturas) — começa
  // direto na aba de Faturas, já que a de Mapas fica escondida.
  const [activeTab, setActiveTab] = useState(isFluxoCavalo ? 'faturas' : 'mapas')

  const [config, setConfig] = useState({
    mapa_comprador_dados_bancarios_vendedor:   false,
    mapa_comprador_versao_padrao:              '1',
    mapa_comprador_destaque_total:             'liquido',
    mapa_comprador_ocultar_total_liquido:      false,
    mapa_comprador_v1_linha_assinatura:        false,
    mapa_comprador_ordenacao:                  'numero_lote',
    mapa_comprador_tipo_padrao:                'padrao',
    mapa_comprador_v1_colunas_ativas:          ['lote', 'vendedor', 'curral', 'situacao', 'qtd', 'comissao', 'valor_unitario', 'total', 'total_com_comissao'],
    mapa_comprador_v2_colunas_ativas:          ['lote', 'curral', 'vendedor', 'situacao', 'qtd', 'descricao', 'valor_unitario', 'total', 'liquido', 'comissao'],
    mapa_vendedor_dados_bancarios_organizador: true,
    mapa_vendedor_dados_bancarios_vendedor:    false,
    mapa_vendedor_versao_padrao:               '1',
    mapa_vendedor_v1_linha_assinatura:         false,
    mapa_vendedor_ordenacao:                   'numero_lote',
    mapa_vendedor_v1_colunas_ativas:           ['lote', 'comprador', 'qtd', 'comissao', 'despesas', 'valor_unitario', 'total', 'liquido'],
    mapa_vendedor_v2_colunas_ativas:           ['lote', 'curral', 'comprador', 'situacao', 'qtd', 'descricao', 'valor_unitario', 'total', 'liquido', 'comissao'],
    fatura_comprador_mostrar_medias_sexo:      false,
    fatura_comprador_mostrar_media_geral:      false,
    fatura_comprador_mostrar_gta_nota:         false,
    fatura_comprador_ocultar_linha_gta_nota:   false,
    fatura_comprador_ocultar_coluna_gado:      false,
    fatura_comprador_comissao_em_linha:         false,
    fatura_comprador_ocultar_parcelas:          false,
    fatura_comprador_ordenar_pgtos_por_data:    false,
    fatura_comprador_total_compra:              'bruto',
    fatura_vendedor_dados_bancarios_vendedor:  false,
    fatura_vendedor_mostrar_medias_sexo:       false,
    fatura_vendedor_mostrar_media_geral:       false,
    fatura_vendedor_ocultar_parcelas:          false,
    fatura_vendedor_total_animais:             null,
    prazo_dias_diferente:                      false,
    mapa_defesas_colunas_ativas:              ['lote', 'qtd', 'vendedor', 'tipo', 'comissao', 'valor_unitario', 'valor_total'],
    mapa_defesas_modo_peso:                   'peso_arroba',
    mapa_indefinidos_colunas_ativas:          ['lote', 'qtd', 'vendedor', 'animal', 'curral'],
    mapa_indefinidos_modo_peso:               'peso_arroba',
    mapa_geral_colunas_ativas:                ['lote', 'vend_comp', 'comissao', 'descricao', 'tipo', 'qtd', 'valor_unitario', 'valor_total', 'valor_liquido'],
    mapa_geral_vendedor_comprador_separados:  false,
    mapa_geral_ocultar_liquido:               false,
    fatura_comprador_colunas_ativas:          ['lote', 'vendedor', 'qtd', 'raca', 'sexo', 'peso', 'valor_unitario', 'valor_total', 'comissao_comprador', 'total_liquido', 'prazo'],
    fatura_vendedor_colunas_ativas:           ['lote', 'qtd', 'sexo', 'raca', 'valor_unitario', 'valor_total', 'comissao_venda', 'total_liquido', 'prazo', 'comprador'],
  })
  const [loading, setLoading] = useState(true)
  const [saving,  setSaving]  = useState(false)
  const [saved,   setSaved]   = useState(false)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    mapasService.getConfig(orgId)
      .then(data => {
        if (!data.mapa_comprador_ordenacao) data.mapa_comprador_ordenacao = 'numero_lote'
        if (!data.mapa_comprador_tipo_padrao) data.mapa_comprador_tipo_padrao = 'padrao'
        if (!data.mapa_comprador_v1_colunas_ativas) data.mapa_comprador_v1_colunas_ativas = ['lote', 'vendedor', 'curral', 'situacao', 'qtd', 'comissao', 'valor_unitario', 'total', 'total_com_comissao']
        if (!data.mapa_comprador_v2_colunas_ativas) data.mapa_comprador_v2_colunas_ativas = ['lote', 'curral', 'vendedor', 'situacao', 'qtd', 'descricao', 'valor_unitario', 'total', 'liquido', 'comissao']
        if (!data.mapa_vendedor_ordenacao) data.mapa_vendedor_ordenacao = 'numero_lote'
        if (!data.mapa_vendedor_v1_colunas_ativas) data.mapa_vendedor_v1_colunas_ativas = ['lote', 'comprador', 'qtd', 'comissao', 'despesas', 'valor_unitario', 'total', 'liquido']
        if (!data.mapa_vendedor_v2_colunas_ativas) data.mapa_vendedor_v2_colunas_ativas = ['lote', 'curral', 'comprador', 'situacao', 'qtd', 'descricao', 'valor_unitario', 'total', 'liquido', 'comissao']
        if (!data.mapa_defesas_colunas_ativas) data.mapa_defesas_colunas_ativas = ['lote', 'qtd', 'vendedor', 'tipo', 'comissao', 'valor_unitario', 'valor_total']
        if (!data.mapa_defesas_modo_peso) data.mapa_defesas_modo_peso = 'peso_arroba'
        if (!data.mapa_indefinidos_colunas_ativas) data.mapa_indefinidos_colunas_ativas = ['lote', 'qtd', 'vendedor', 'animal', 'curral']
        if (!data.mapa_indefinidos_modo_peso) data.mapa_indefinidos_modo_peso = 'peso_arroba'
        if (!data.mapa_geral_colunas_ativas) data.mapa_geral_colunas_ativas = ['lote', 'vend_comp', 'comissao', 'descricao', 'tipo', 'qtd', 'valor_unitario', 'valor_total', 'valor_liquido']
        if (!data.fatura_comprador_colunas_ativas) data.fatura_comprador_colunas_ativas = ['lote', 'vendedor', 'qtd', 'raca', 'sexo', 'peso', 'valor_unitario', 'valor_total', 'comissao_comprador', 'total_liquido', 'prazo']
        if (!data.fatura_vendedor_colunas_ativas) data.fatura_vendedor_colunas_ativas = ['lote', 'qtd', 'sexo', 'raca', 'valor_unitario', 'valor_total', 'comissao_venda', 'total_liquido', 'prazo', 'comprador']
        setConfig(c => ({ ...c, ...data }))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [orgId])

  const set = (key, val) => setConfig(p => ({ ...p, [key]: val }))

  const handleSave = async () => {
    if (!orgId) return
    setSaving(true)
    try {
      await mapasService.updateConfig(orgId, config)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (e) {
      alert('Erro ao salvar: ' + e.message)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return (
    <div className="flex justify-center items-center h-40">
      <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
    </div>
  )

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Settings2 className="w-5 h-5 text-cyan-600" />
          <h1 className="text-lg font-semibold text-gray-800">Configurações de Impressão</h1>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? 'Salvando...' : saved ? 'Salvo ✓' : 'Salvar'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1">
        <TabButton active={activeTab === 'mapas'} onClick={() => setActiveTab('mapas')} icon={Map} label="Mapas" />
        <TabButton active={activeTab === 'faturas'} onClick={() => setActiveTab('faturas')} icon={FileText} label="Faturas" />
        <TabButton active={activeTab === 'geral'} onClick={() => setActiveTab('geral')} icon={CreditCard} label="Geral" />
      </div>

      {/* ═══════════════ ABA MAPAS ═══════════════ */}
      {activeTab === 'mapas' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Mapa do Comprador / Mapa do Vendedor — fluxo cavalo trabalha só
              com faturas, esses mapas não se aplicam (ver aba Faturas). */}
          {!isFluxoCavalo && (<>

          {/* Mapa do Comprador */}
          <Section icon={Map} title="Mapa do Comprador" subtitle="Padrões aplicados ao gerar o mapa" color="text-cyan-600">
            <Toggle checked={config.mapa_comprador_dados_bancarios_vendedor} onChange={v => set('mapa_comprador_dados_bancarios_vendedor', v)} label="Incluir dados bancários do vendedor" description="Exibe os dados bancários do vendedor no rodapé do mapa" />
            <div className="py-3">
              <p className="text-sm font-medium text-gray-800 mb-1">Colunas visíveis — Versão 1</p>
              <p className="text-xs text-gray-400 mb-2">Layout clássico com situação e comissão</p>
              <ColumnGrid
                columns={[
                  { id: 'lote',               label: 'Lote',            obrigatoria: true },
                  { id: 'vendedor',            label: 'Vendedor',        obrigatoria: true },
                  { id: 'curral',              label: labelCurral,          obrigatoria: false },
                  { id: 'situacao',            label: 'Situação',        obrigatoria: false },
                  { id: 'qtd',                 label: 'Qtd',             obrigatoria: false },
                  { id: 'pct',                 label: '% Comissão',      obrigatoria: false },
                  { id: 'comissao',            label: 'Comissão',        obrigatoria: false },
                  { id: 'valor_unitario',      label: 'Valor Unitário',  obrigatoria: false },
                  { id: 'total',               label: 'Total',           obrigatoria: false },
                  { id: 'total_com_comissao',  label: 'Total + Com.',    obrigatoria: false },
                ]}
                activeColumns={config.mapa_comprador_v1_colunas_ativas}
                configKey="mapa_comprador_v1_colunas_ativas"
                set={set}
                config={config}
              />
            </div>
            <div className="py-3">
              <p className="text-sm font-medium text-gray-800 mb-1">Colunas visíveis — Versão 2</p>
              <p className="text-xs text-gray-400 mb-2">Layout moderno com descrição e comissões</p>
              <ColumnGrid
                columns={[
                  { id: 'lote',          label: 'Lote',          obrigatoria: true },
                  { id: 'curral',        label: labelCurral,         obrigatoria: false },
                  { id: 'vendedor',      label: 'Vendedor',       obrigatoria: true },
                  { id: 'situacao',      label: 'Situação',       obrigatoria: false },
                  { id: 'qtd',           label: 'Qtd',            obrigatoria: false },
                  { id: 'descricao',     label: 'Descrição',      obrigatoria: false },
                  { id: 'valor_unitario',label: 'Unitário',       obrigatoria: false },
                  { id: 'total',         label: 'Total',          obrigatoria: false },
                  { id: 'liquido',       label: 'Líquido',        obrigatoria: false },
                  { id: 'comissao',      label: 'Comissões',      obrigatoria: false },
                  { id: 'pct',           label: '% Comissão',     obrigatoria: false },
                ]}
                activeColumns={config.mapa_comprador_v2_colunas_ativas}
                configKey="mapa_comprador_v2_colunas_ativas"
                set={set}
                config={config}
              />
            </div>
            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div className="flex-1 pr-4">
                <p className="text-sm font-medium text-gray-800">Valor em destaque no Total Geral</p>
                <p className="text-xs text-gray-500 mt-0.5">Qual célula recebe o fundo azul (fica por último)</p>
              </div>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm flex-shrink-0">
                {[['liquido', 'Total Líquido'], ['total_mais_comissoes', 'Total + Comissões']].map(([v, lbl]) => (
                  <button key={v} type="button" onClick={() => set('mapa_comprador_destaque_total', v)}
                    className={`px-3 py-1.5 font-medium transition-colors ${config.mapa_comprador_destaque_total === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
            <Toggle checked={config.mapa_comprador_ocultar_total_liquido} onChange={v => set('mapa_comprador_ocultar_total_liquido', v)} label='Ocultar "Total Líquido" no Total Geral' description="Remove a célula de Total Líquido do bloco de totais (não aplicável ao fluxo separada)" />
            <Toggle checked={!!config.mapa_comprador_v1_linha_assinatura} onChange={v => set('mapa_comprador_v1_linha_assinatura', v)} label='Linha de assinatura do comprador — Versão 1' description="Adiciona linha de assinatura com nome e fazenda do comprador ao final do PDF" />
            <div className="flex items-center justify-between py-3">
              <div className="flex-1 pr-4">
                <p className="text-sm font-medium text-gray-800">Versão padrão do layout</p>
                <p className="text-xs text-gray-500 mt-0.5">Versão pré-selecionada ao abrir o mapa</p>
              </div>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm flex-shrink-0">
                {['1', '2'].map(v => (
                  <button key={v} type="button" onClick={() => set('mapa_comprador_versao_padrao', v)}
                    className={`px-4 py-1.5 font-medium transition-colors ${config.mapa_comprador_versao_padrao === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    Versão {v}
                  </button>
                ))}
              </div>
            </div>
            <div className="py-3 border-t border-gray-100">
              <p className="text-sm font-medium text-gray-800">Tipo padrão</p>
              <p className="text-xs text-gray-500 mt-0.5 mb-2">Tipo pré-selecionado ao abrir o mapa (Compras e Defesas inclui também quem só comprou ou só teve defesa)</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'padrao', label: 'Só Compras' },
                  { id: 'vendas_defesa', label: 'Compras e Defesas' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => set('mapa_comprador_tipo_padrao', opt.id)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                      config.mapa_comprador_tipo_padrao === opt.id
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2 py-3 border-t border-gray-100">
              <ArrowUpDown className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-medium text-gray-800">Ordenação dos lotes</p>
            </div>
            <div className="flex flex-wrap gap-2 pb-3">
              {[
                { id: 'numero_lote', label: 'Nº do Lote' },
                { id: 'nome_vendedor', label: 'Nome do Vendedor' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => set('mapa_comprador_ordenacao', opt.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                    config.mapa_comprador_ordenacao === opt.id
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Section>

          {/* Mapa do Vendedor */}
          <Section icon={Store} title="Mapa do Vendedor" subtitle="Padrões aplicados ao gerar o mapa" color="text-emerald-600">
            <Toggle checked={config.mapa_vendedor_dados_bancarios_organizador} onChange={v => set('mapa_vendedor_dados_bancarios_organizador', v)} label="Incluir dados bancários do organizador" description="Exibe os dados bancários do organizador no mapa do vendedor" />
            <Toggle checked={config.mapa_vendedor_dados_bancarios_vendedor} onChange={v => set('mapa_vendedor_dados_bancarios_vendedor', v)} label="Incluir dados bancários do vendedor" description="Exibe os dados bancários do vendedor na seção de dados do mapa" />
            <div className="py-3">
              <p className="text-sm font-medium text-gray-800 mb-1">Colunas visíveis — Versão 1</p>
              <p className="text-xs text-gray-400 mb-2">Layout clássico com comprador e despesas</p>
              <ColumnGrid
                columns={[
                  { id: 'lote',          label: 'Lote',           obrigatoria: true },
                  { id: 'curral',        label: 'Curral',         obrigatoria: false },
                  { id: 'comprador',     label: 'Comprador',      obrigatoria: true },
                  { id: 'qtd',           label: 'Qtd',            obrigatoria: false },
                  { id: 'comissao',      label: 'Com. de Venda',  obrigatoria: false },
                  { id: 'despesas',      label: 'Despesas',       obrigatoria: false },
                  { id: 'valor_unitario',label: 'Valor Unitário', obrigatoria: false },
                  { id: 'total',         label: 'Total',          obrigatoria: false },
                  { id: 'liquido',       label: 'Líquido',        obrigatoria: false },
                ]}
                activeColumns={config.mapa_vendedor_v1_colunas_ativas}
                configKey="mapa_vendedor_v1_colunas_ativas"
                set={set}
                config={config}
              />
            </div>
            <div className="py-3">
              <p className="text-sm font-medium text-gray-800 mb-1">Colunas visíveis — Versão 2</p>
              <p className="text-xs text-gray-400 mb-2">Layout moderno com descrição e comissões</p>
              <ColumnGrid
                columns={[
                  { id: 'lote',          label: 'Lote',          obrigatoria: true },
                  { id: 'curral',        label: labelCurral,         obrigatoria: false },
                  { id: 'comprador',     label: 'Comprador',      obrigatoria: true },
                  { id: 'situacao',      label: 'Situação',       obrigatoria: false },
                  { id: 'qtd',           label: 'Qtd',            obrigatoria: false },
                  { id: 'descricao',     label: 'Descrição',      obrigatoria: false },
                  { id: 'valor_unitario',label: 'Unitário',       obrigatoria: false },
                  { id: 'total',         label: 'Total',          obrigatoria: false },
                  { id: 'liquido',       label: 'Líquido',        obrigatoria: false },
                  { id: 'comissao',      label: 'Comissões',      obrigatoria: false },
                  { id: 'pct',           label: '% Comissão',     obrigatoria: false },
                ]}
                activeColumns={config.mapa_vendedor_v2_colunas_ativas}
                configKey="mapa_vendedor_v2_colunas_ativas"
                set={set}
                config={config}
              />
            </div>
            <Toggle checked={!!config.mapa_vendedor_v1_linha_assinatura} onChange={v => set('mapa_vendedor_v1_linha_assinatura', v)} label='Linha de assinatura do vendedor — Versão 1' description="Adiciona linha de assinatura com nome e fazenda do vendedor ao final do PDF" />
            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div className="flex-1 pr-4">
                <p className="text-sm font-medium text-gray-800">Versão padrão do layout</p>
                <p className="text-xs text-gray-500 mt-0.5">Versão pré-selecionada ao abrir o mapa</p>
              </div>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm flex-shrink-0">
                {['1', '2'].map(v => (
                  <button key={v} type="button" onClick={() => set('mapa_vendedor_versao_padrao', v)}
                    className={`px-4 py-1.5 font-medium transition-colors ${config.mapa_vendedor_versao_padrao === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                    Versão {v}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2 py-3 border-t border-gray-100">
              <ArrowUpDown className="w-4 h-4 text-gray-500" />
              <p className="text-sm font-medium text-gray-800">Ordenação dos lotes</p>
            </div>
            <div className="flex flex-wrap gap-2 pb-3">
              {[
                { id: 'numero_lote', label: 'Nº do Lote' },
                { id: 'nome_comprador', label: 'Nome do Comprador' },
              ].map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => set('mapa_vendedor_ordenacao', opt.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-all ${
                    config.mapa_vendedor_ordenacao === opt.id
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </Section>

          </>)}

          {/* Mapa Geral */}
          <Section icon={Map} title="Mapa Geral" subtitle="Colunas e layout do mapa geral de lotes" color="text-indigo-600">
            <div className="py-3">
              <p className="text-sm font-medium text-gray-800 mb-3">Colunas visíveis <span className="text-xs text-gray-400 font-normal">(máx. 9 para caber no A4)</span></p>
              <div className="grid grid-cols-2 gap-2">
                {(() => {
                  const separados = config.mapa_geral_vendedor_comprador_separados
                  const colunas = [
                    { id: 'lote', label: 'Lote', obrigatoria: true },
                    ...(!separados ? [{ id: 'vend_comp', label: 'Vend. / Comp.', obrigatoria: false }] : []),
                    ...(separados ? [{ id: 'vendedor', label: 'Vendedor', obrigatoria: false }] : []),
                    ...(separados ? [{ id: 'comprador', label: 'Comprador', obrigatoria: false }] : []),
                    { id: 'comissao', label: 'Comissão (%)', obrigatoria: false },
                    { id: 'descricao', label: 'Descrição', obrigatoria: false },
                    { id: 'tipo', label: 'Tipo/Situação', obrigatoria: false },
                    { id: 'qtd', label: 'Qtd', obrigatoria: false },
                    { id: 'valor_unitario', label: 'Valor Unitário', obrigatoria: false },
                    { id: 'valor_total', label: 'Valor Total', obrigatoria: false },
                    { id: 'valor_liquido', label: 'Valor Líquido', obrigatoria: false },
                  ]
                  const ativas = config.mapa_geral_colunas_ativas || []
                  const totalAtivas = ativas.filter(c => colunas.some(col => col.id === c)).length

                  return colunas.map(col => {
                    const isActive = ativas.includes(col.id)
                    const limitReached = totalAtivas >= 9 && !isActive
                    return (
                      <div key={col.id}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all ${isActive ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50'} ${col.obrigatoria || limitReached ? 'opacity-75 cursor-not-allowed' : 'hover:border-blue-300'}`}
                        onClick={() => {
                          if (col.obrigatoria) return
                          if (limitReached && !isActive) return
                          const current = config.mapa_geral_colunas_ativas || []
                          const updated = isActive ? current.filter(c => c !== col.id) : [...current, col.id]
                          set('mapa_geral_colunas_ativas', updated)
                        }}
                      >
                        {isActive ? <Eye className="w-4 h-4 text-blue-600 flex-shrink-0" /> : <EyeOff className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                        <span className={`text-xs font-medium ${isActive ? 'text-blue-900' : 'text-gray-600'}`}>{col.label}</span>
                        {col.obrigatoria && <span className="text-[10px] bg-yellow-100 text-yellow-800 px-1 rounded">Obrig.</span>}
                        {limitReached && !isActive && <span className="text-[10px] bg-red-100 text-red-700 px-1 rounded">Máx.</span>}
                      </div>
                    )
                  })
                })()}
              </div>
            </div>
            <Toggle
              checked={config.mapa_geral_vendedor_comprador_separados}
              onChange={v => {
                set('mapa_geral_vendedor_comprador_separados', v)
                const current = config.mapa_geral_colunas_ativas || []
                if (v) {
                  let updated = current.filter(c => c !== 'vend_comp' && c !== 'valor_liquido')
                  if (!updated.includes('vendedor')) updated.push('vendedor')
                  if (!updated.includes('comprador')) updated.push('comprador')
                  set('mapa_geral_colunas_ativas', updated)
                } else {
                  const updated = current.filter(c => c !== 'vendedor' && c !== 'comprador')
                  if (!updated.includes('vend_comp')) updated.push('vend_comp')
                  set('mapa_geral_colunas_ativas', updated)
                }
              }}
              label="Vendedor e Comprador em colunas separadas"
              description="Quando ativado, exibe Vendedor e Comprador em colunas distintas ao invés de mesclados"
            />
            <Toggle checked={config.mapa_geral_ocultar_liquido ?? false} onChange={v => set('mapa_geral_ocultar_liquido', v)} label="Ocultar linha Líquido no resumo" description="Remove a linha de total Líquido do bloco de resumo ao final do mapa" />
          </Section>

          {/* Mapa Geral de Defesas */}
          <Section icon={Shield} title="Mapa Geral de Defesas" subtitle="Colunas e layout do mapa de defesas" color="text-red-600">
            <div className="py-3">
              <p className="text-sm font-medium text-gray-800 mb-3">Colunas visíveis</p>
              <ColumnGrid
                columns={[
                  { id: 'lote', label: 'Lote', obrigatoria: true },
                  { id: 'qtd', label: 'Qtd', obrigatoria: true },
                  { id: 'sexo', label: 'Sexo', obrigatoria: false },
                  { id: 'idade', label: 'Idade', obrigatoria: false },
                  { id: 'vendedor', label: 'Vendedor', obrigatoria: false },
                  { id: 'fazenda', label: 'Fazenda', obrigatoria: false },
                  { id: 'tipo', label: 'Tipo', obrigatoria: false },
                  ...(isFluxoCavalo ? [{ id: 'animal', label: 'Animal', obrigatoria: false }] : []),
                  { id: 'peso', label: 'Peso', obrigatoria: false },
                  { id: 'comissao', label: 'Comissão', obrigatoria: false },
                  ...(isFluxoCavalo ? [{ id: 'despesas', label: 'Despesas', obrigatoria: false }] : []),
                  { id: 'valor_unitario', label: 'Valor Unitário', obrigatoria: false },
                  { id: 'valor_total', label: 'Valor Total', obrigatoria: false },
                ]}
                activeColumns={config.mapa_defesas_colunas_ativas}
                configKey="mapa_defesas_colunas_ativas"
                set={set}
                config={config}
              />
            </div>
            <div className="py-3">
              <p className="text-sm font-medium text-gray-800 mb-2">Exibição do Peso</p>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                <button type="button" onClick={() => set('mapa_defesas_modo_peso', 'peso')} className={`flex-1 px-3 py-1.5 font-medium transition-colors ${config.mapa_defesas_modo_peso === 'peso' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Só Peso (Kg)</button>
                <button type="button" onClick={() => set('mapa_defesas_modo_peso', 'peso_arroba')} className={`flex-1 px-3 py-1.5 font-medium transition-colors ${config.mapa_defesas_modo_peso === 'peso_arroba' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Peso + @</button>
              </div>
            </div>
          </Section>

          {/* Mapa Geral de Indefinidos */}
          <Section icon={Shield} title="Mapa Geral de Indefinidos" subtitle="Colunas e layout do mapa de indefinidos" color="text-gray-600">
            <div className="py-3">
              <p className="text-sm font-medium text-gray-800 mb-3">Colunas visíveis</p>
              <ColumnGrid
                columns={[
                  { id: 'lote', label: 'Lote', obrigatoria: true },
                  { id: 'vendedor', label: 'Vendedor', obrigatoria: false },
                  { id: 'animal', label: 'Animal', obrigatoria: false },
                  { id: 'curral', label: labelCurral, obrigatoria: false },
                  { id: 'qtd', label: 'Qtd', obrigatoria: true },
                  { id: 'peso', label: 'Peso', obrigatoria: false },
                  ...(isFluxoCavalo ? [{ id: 'despesas', label: 'Despesas', obrigatoria: false }] : []),
                ]}
                activeColumns={config.mapa_indefinidos_colunas_ativas}
                configKey="mapa_indefinidos_colunas_ativas"
                set={set}
                config={config}
              />
            </div>
            <div className="py-3">
              <p className="text-sm font-medium text-gray-800 mb-2">Exibição do Peso</p>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm">
                <button type="button" onClick={() => set('mapa_indefinidos_modo_peso', 'peso')} className={`flex-1 px-3 py-1.5 font-medium transition-colors ${config.mapa_indefinidos_modo_peso === 'peso' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Só Peso (Kg)</button>
                <button type="button" onClick={() => set('mapa_indefinidos_modo_peso', 'peso_arroba')} className={`flex-1 px-3 py-1.5 font-medium transition-colors ${config.mapa_indefinidos_modo_peso === 'peso_arroba' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Peso + @</button>
              </div>
            </div>
          </Section>

        </div>
      )}

      {/* ═══════════════ ABA FATURAS ═══════════════ */}
      {activeTab === 'faturas' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Fatura do Comprador */}
          <Section icon={FileText} title="Fatura do Comprador" subtitle="Colunas e blocos exibidos na fatura" color="text-blue-600">
            <div className="py-3">
              <p className="text-sm font-medium text-gray-800 mb-3">Colunas visíveis na tabela de lotes <span className="text-xs text-gray-400 font-normal">(máx. 10)</span></p>
              <ColumnGrid
                columns={[
                  { id: 'lote', label: 'Lote', obrigatoria: true },
                  { id: 'vendedor', label: 'Vendedor', obrigatoria: false },
                  { id: 'qtd', label: 'Qtd', obrigatoria: false },
                  { id: 'tipo', label: 'Tipo', obrigatoria: false },
                  { id: 'raca', label: 'Raça', obrigatoria: false },
                  ...(isFluxoCavalo ? [{ id: 'animal', label: 'Animal', obrigatoria: false }] : []),
                  { id: 'sexo', label: 'Sexo', obrigatoria: false },
                  { id: 'peso', label: 'Peso', obrigatoria: false },
                  { id: 'valor_unitario', label: 'Valor Unit.', obrigatoria: false },
                  { id: 'valor_total', label: 'Vlr Total Lote', obrigatoria: false },
                  { id: 'comissao_comprador', label: 'Valor Com. Comp.', obrigatoria: false },
                  { id: 'total_liquido', label: 'Total +/- Com.', obrigatoria: false },
                  { id: 'prazo', label: 'Prazo', obrigatoria: false },
                ]}
                activeColumns={config.fatura_comprador_colunas_ativas}
                configKey="fatura_comprador_colunas_ativas"
                set={set}
                config={config}
                maxColumns={10}
              />
            </div>
            <div className="py-2 border-t border-gray-100 mt-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2 pb-1">Blocos e opções</p>
            </div>
            <Toggle checked={config.fatura_comprador_mostrar_medias_sexo} onChange={v => set('fatura_comprador_mostrar_medias_sexo', v)} label="Médias por Sexo" description="Exibe a tabela de médias agrupadas por sexo" />
            <Toggle checked={config.fatura_comprador_mostrar_media_geral} onChange={v => set('fatura_comprador_mostrar_media_geral', v)} label="Média Geral" description="Exibe o bloco de média geral do comprador" />
            <Toggle checked={config.fatura_comprador_mostrar_gta_nota} onChange={v => set('fatura_comprador_mostrar_gta_nota', v)} label="Cálculo GTA e Nota" description="Exibe o bloco de cálculo de GTA e Nota Fiscal" />
            <Toggle checked={config.fatura_comprador_ocultar_linha_gta_nota} onChange={v => set('fatura_comprador_ocultar_linha_gta_nota', v)} label="Ocultar linha GTA + Nota na comissão" description="Oculta a linha 'GTA + Nota' no bloco de pagamento (valor continua no total)" />
            <Toggle checked={config.fatura_comprador_ocultar_coluna_gado} onChange={v => set('fatura_comprador_ocultar_coluna_gado', v)} label='Ocultar coluna "Gado + Com. + GTA/Nota"' description="Remove a coluna de valor total do gado + comissões do bloco de pagamento" />
            <Toggle checked={config.fatura_comprador_comissao_em_linha} onChange={v => set('fatura_comprador_comissao_em_linha', v)} label="Comissão em linha" description="Remove a tabela 'Pagamento Comissão' e inclui a comissão como primeira linha na tabela 'Lista de Pagamentos'" />
            <Toggle checked={config.fatura_comprador_ocultar_parcelas} onChange={v => set('fatura_comprador_ocultar_parcelas', v)} label='Ocultar indicador "Parcelas: N"' description="Oculta a linha com a quantidade de parcelas na célula do vendedor (seção Pagamentos Vendedores)" />
            <div className="flex items-center justify-between py-3">
              <div className="flex-1 pr-4">
                <p className="text-sm font-medium text-gray-800">Total Compra (TOTAL GERAL)</p>
                <p className="text-xs text-gray-500 mt-0.5">"Bruto" soma o valor dos lotes; "Líquido" soma a coluna "Total - Com. Ven." (já descontadas comissão de venda e desconto)</p>
              </div>
              <select
                value={config.fatura_comprador_total_compra || 'bruto'}
                onChange={e => set('fatura_comprador_total_compra', e.target.value)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="bruto">Total Compra (bruto)</option>
                <option value="liquido">Total Compra Líquido</option>
              </select>
            </div>
            <Toggle checked={config.fatura_comprador_ordenar_pgtos_por_data} onChange={v => set('fatura_comprador_ordenar_pgtos_por_data', v)} label='Ordenar pagamentos por data de vencimento' description="Ordena a lista de pagamentos vendedores pela data de vencimento (ao invés do número do lote)" />
          </Section>

          {/* Fatura do Vendedor */}
          <Section icon={FileText} title="Fatura do Vendedor" subtitle="Colunas e blocos exibidos na fatura" color="text-green-600">
            <div className="py-3">
              <p className="text-sm font-medium text-gray-800 mb-3">Colunas visíveis na tabela de lotes <span className="text-xs text-gray-400 font-normal">(máx. 10)</span></p>
              <ColumnGrid
                columns={[
                  { id: 'lote', label: 'Lote', obrigatoria: true },
                  { id: 'qtd', label: 'Qtd', obrigatoria: false },
                  { id: 'tipo', label: 'Tipo', obrigatoria: false },
                  { id: 'sexo', label: 'Sexo', obrigatoria: false },
                  { id: 'raca', label: 'Raça', obrigatoria: false },
                  ...(isFluxoCavalo ? [{ id: 'animal', label: 'Animal', obrigatoria: false }] : []),
                  { id: 'valor_unitario', label: 'Valor Unit.', obrigatoria: false },
                  { id: 'valor_total', label: 'Vlr Total (Venda)', obrigatoria: false },
                  { id: 'comissao_venda', label: 'Com. Venda', obrigatoria: false },
                  { id: 'despesas', label: 'Despesas', obrigatoria: false },
                  { id: 'total_liquido', label: 'Total Líquido', obrigatoria: false },
                  { id: 'prazo', label: 'Prazo', obrigatoria: false },
                  { id: 'comprador', label: 'Comprador', obrigatoria: false },
                ]}
                activeColumns={config.fatura_vendedor_colunas_ativas}
                configKey="fatura_vendedor_colunas_ativas"
                set={set}
                config={config}
                maxColumns={10}
              />
            </div>
            <div className="py-2 border-t border-gray-100 mt-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2 pb-1">Blocos e opções</p>
            </div>
            <Toggle checked={config.fatura_vendedor_dados_bancarios_vendedor} onChange={v => set('fatura_vendedor_dados_bancarios_vendedor', v)} label="Dados bancários do vendedor" description="Exibe os dados bancários do vendedor no cabeçalho da fatura (sempre visível quando comissão separada)" />
            <Toggle checked={config.fatura_vendedor_mostrar_medias_sexo} onChange={v => set('fatura_vendedor_mostrar_medias_sexo', v)} label="Médias por Sexo" description="Exibe a tabela de médias agrupadas por sexo" />
            <Toggle checked={config.fatura_vendedor_mostrar_media_geral} onChange={v => set('fatura_vendedor_mostrar_media_geral', v)} label="Média Geral" description="Exibe o bloco de média geral do vendedor" />
            <div className="flex items-center justify-between py-3">
              <div className="flex-1 pr-4">
                <p className="text-sm font-medium text-gray-800">Totais de animais (TOTAL GERAL)</p>
                <p className="text-xs text-gray-500 mt-0.5">"Total Animais" inclui defesas e não movimentados; "Animais Vendidos" conta só os lotes vendidos</p>
              </div>
              <select
                value={config.fatura_vendedor_total_animais || ''}
                onChange={e => set('fatura_vendedor_total_animais', e.target.value || null)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Padrão (gado: Total / cavalo: ambos)</option>
                <option value="total">Total Animais</option>
                <option value="vendidos">Animais Vendidos</option>
                <option value="ambos">Ambos</option>
              </select>
            </div>
            <Toggle checked={config.fatura_vendedor_ocultar_parcelas} onChange={v => set('fatura_vendedor_ocultar_parcelas', v)} label='Ocultar indicador "Parcelas: N"' description="Oculta a linha com a quantidade de parcelas na célula do comprador (seção Listagem dos Pagamentos)" />
          </Section>

        </div>
      )}

      {/* ═══════════════ ABA GERAL ═══════════════ */}
      {activeTab === 'geral' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section icon={CreditCard} title="Pagamento" subtitle="Configurações de prazo e modalidades" color="text-purple-600">
            <Toggle
              checked={config.prazo_dias_diferente}
              onChange={v => set('prazo_dias_diferente', v)}
              label="Prazo em dias diferente para Comprador e Vendedor"
              description="Quando ativado, o faturamento com Prazo (dias corridos) exibe dois campos: prazo do comprador e prazo do vendedor. O prazo do comprador é usado em faturas, contratos, notas e mapas. O prazo do vendedor aparece apenas na fatura do vendedor."
            />
          </Section>
        </div>
      )}

    </div>
  )
}
